import { Col } from 'antd';
import { DonorOutput, UserRole } from 'gql/types';
import { FormItem } from 'modules/common/components';
import { useUpdateDonorsOwnerModalContext } from 'modules/donor/contexts/UpdateDonorsOwnerModal';
import {
  getAllowedDonorAge,
  shouldRetireDonor,
} from 'modules/donor/utils/get-allowed-donor-age';
import { useOwnerData } from 'modules/owner/hooks/useOwnerData';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getFields } from './fields';

type PropTypes = {
  fieldNamePrefix: string;
  disabled?: boolean;
  ownerId?: string;
  donor?: DonorOutput;
  validateAge?: boolean;
};

const RequiredFormSection: FC<PropTypes> = ({
  fieldNamePrefix,
  disabled,
  ownerId,
  donor,
}) => {
  const { t } = useTranslation('donor.EditDonorForm.RequiredSection');

  const { control, formState, getFieldState } = useFormContext();

  const currentLocation = useWatch({
    control,
    name: `${fieldNamePrefix}.location`,
  });
  const currentSpecies = useWatch({
    control,
    name: `${fieldNamePrefix}.species`,
  });
  const currentAge = useWatch({
    control,
    name: `${fieldNamePrefix}.age`,
  });

  const initialLocation = donor?.location;

  const { hasMultipleDonors } = useOwnerData(ownerId);

  const locationWasChanged =
    Boolean(currentLocation) &&
    Boolean(initialLocation) &&
    currentLocation !== initialLocation;

  const showLocationWarning =
    !disabled && hasMultipleDonors && locationWasChanged;

  const donorAgeRange = getAllowedDonorAge(currentSpecies);
  const showAgeWarning = shouldRetireDonor(currentSpecies, currentAge);

  const currentUser = useCurrentUser();
  const isCurrentUserAdmin = currentUser.data?.role.value === UserRole.Admin;
  const updateDonorsOwnerModal = useUpdateDonorsOwnerModalContext();

  const requiredFormSectionFields = useMemo(() => {
    return getFields({
      onEditOwnerClick: updateDonorsOwnerModal?.showModal,
      isEditOwnerAllowed: isCurrentUserAdmin,
    });
  }, [isCurrentUserAdmin]);

  const getExtraText = ({
    errorMessage,
    isLocationField,
    isDateOfBirthField,
    showAgeWarning,
    showLocationWarning,
  }: {
    errorMessage?: string;
    isLocationField: boolean;
    isDateOfBirthField: boolean;
    showAgeWarning: boolean;
    showLocationWarning: boolean;
  }): string | undefined => {
    if (errorMessage) {
      return errorMessage;
    }

    if (isLocationField && showLocationWarning) {
      return t('changeLocationWarning');
    }

    if (isDateOfBirthField && showAgeWarning) {
      return t('donorAgeIsOutOfRangeWarning', donorAgeRange);
    }

    return undefined;
  };

  return (
    <>
      {requiredFormSectionFields.map(({ Component, label, name }) => {
        const fieldName = `${fieldNamePrefix}.${name}`;
        const { error } = getFieldState(fieldName, formState);
        const isLocationField = name === 'location';
        const isDateOfBirthField = name === 'dateOfBirth';

        const extraText = getExtraText({
          errorMessage: error?.message,
          isLocationField,
          isDateOfBirthField,
          showAgeWarning,
          showLocationWarning,
        });

        const validateStatus =
          (error && 'error') ||
          (isLocationField && showLocationWarning && 'warning') ||
          (isDateOfBirthField && showAgeWarning && 'warning') ||
          undefined;
        return (
          <Col span={24} key={fieldName}>
            <FormItem
              label={label}
              extra={extraText}
              validateStatus={validateStatus}
            >
              <Component
                control={control}
                name={fieldName}
                disabled={disabled}
              />
            </FormItem>
          </Col>
        );
      })}
    </>
  );
};

export default RequiredFormSection;
